
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Phone, Hammer, Trees, Ruler, Facebook } from 'lucide-react';
import { motion } from 'framer-motion';
import { services, galleryImages, companyInfo } from '../data';
import ContactForm from '../components/ContactForm';

const Home = () => {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[750px] flex items-center overflow-hidden bg-[#1b4c2e]">
        <div className="absolute inset-0 z-0">
          <motion.div
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5 }}
            className="absolute inset-0"
          >
            <img 
              src="/kundebilleder/imgi_67_668492806_122134739943028357_6697783164957947626_ntilpasset.jpg" 
              alt="Smith Have & Anlæg ApS - Professionel Anlægsgartner" 
              className="w-full h-full object-cover"
            />
            {/* Sophisticated gradient overlay for better text readability on the left */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-black/20" />
          </motion.div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10 pt-20">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="space-y-8"
            >
              <div>
                <h3 className="text-green-400 uppercase tracking-[0.2em] text-xs sm:text-sm font-bold mb-4">
                  Velkommen til Smith Have & Anlæg ApS
                </h3>
                <h1 className="text-4xl md:text-[2.5rem] font-light text-white leading-tight tracking-tight">
                  Vi skaber <br />
                  <span className="text-green-400 font-medium">ekstraordinære</span> <br />
                  uderum
                </h1>
              </div>

              <p className="text-xl md:text-2xl text-gray-200 max-w-xl leading-relaxed font-medium">
                Professionel brolægning, solide støttemure og smukke plæner. Vi forvandler din have til din yndlingsplads.
              </p>

              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 pt-4">
                <Link 
                  to="/kontakt" 
                  className="group bg-green-600 hover:bg-green-500 text-white w-[289px] sm:w-fit px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-bold text-sm sm:text-base transition-all shadow-xl flex items-center justify-center gap-3"
                >
                  Få et uforpligtende tilbud
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/galleri" 
                  className="bg-white/10 backdrop-blur-md hover:bg-white/20 text-white border border-white/30 w-[289px] sm:w-fit px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-bold text-sm sm:text-base transition-all flex items-center justify-center gap-3"
                >
                  Se galleri
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-8 right-8 z-20 flex flex-col items-center gap-3"
        >
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/50 to-white" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-white [writing-mode:vertical-lr]">Scroll ned</span>
        </motion.div>
      </section>


      {/* Trust Badges */}
      <section className="bg-gray-50 py-10 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {[
              { icon: CheckCircle2, text: "Høj Kvalitet" },
              { icon: Hammer, text: "Solidt Håndværk" },
              { icon: Trees, text: "Erfarne Folk" },
              { icon: Ruler, text: "Præcis Opfyldelse" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-gray-700 font-semibold">
                <item.icon className="text-green-600" size={24} />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Vores Ydelser</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Vi tilbyder et bredt udvalg af anlægsopgaver med fokus på kvalitet og holdbare løsninger.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all"
              >
                {service.image && service.image !== "" && (
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  <Link 
                    to={`/ydelser/${service.slug}`} 
                    className="text-green-700 font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all"
                  >
                    Læs mere <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Focus on Anlægsgartner */}
      <section className="py-20 bg-[#1b4c2e] text-white relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="lg:w-1/2 space-y-6">
              <h2 className="text-3xl md:text-5xl font-bold leading-tight">
                Din lokale anlægsgartner i Pandrup og omegn
              </h2>
              <p className="text-green-100 text-lg">
                Vi tager os af alt fra den store indkørsel til den hyggelige terrasse. 
                Vores mål er at skabe uderum, som vores kunder kan nyde i mange år fremover.
              </p>
              <ul className="space-y-3">
                {[
                  "Altid faste priser",
                  "Overholder alle aftaler",
                  "Oprydning efter endt arbejde",
                  "Fokus på din tilfredshed"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 font-medium">
                    <div className="bg-green-600 rounded-full p-1">
                      <CheckCircle2 size={16} />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:w-1/3 w-full">
              <div className="bg-white p-8 rounded-3xl text-gray-900 shadow-2xl">
                <h3 className="text-2xl font-bold mb-6 text-center">Bliv kontaktet i dag</h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
        {/* Abstract shapes for background */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#245e39] rounded-full -mr-32 -mt-32 blur-3xl opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#245e39] rounded-full -ml-32 -mb-32 blur-3xl opacity-50" />
      </section>

      {/* Gallery Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Se vores arbejde</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Udvalgte referencer fra tidligere projekter i Nordjylland.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {galleryImages.filter(img => img && img !== "").map((img, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="aspect-square rounded-2xl overflow-hidden"
              >
                <img 
                  src={img} 
                  alt={`Projekt reference ${i + 1}`} 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center flex flex-col items-center gap-6">
            <Link 
              to="/galleri" 
              className="bg-green-600 hover:bg-green-500 text-white w-[289px] sm:w-fit px-6 py-3 rounded-xl font-bold text-sm sm:text-base transition-all shadow-xl flex items-center justify-center gap-3"
            >
              Se hele galleriet
              <ArrowRight size={18} />
            </Link>
            
            <a 
              href={companyInfo.facebook} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-green-700 font-bold hover:underline text-sm"
            >
              <Facebook size={18} />
              Se flere billeder på vores Facebook-side
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
